import React, { createContext, useContext, useReducer, useEffect, useState } from 'react';
import useLocalStorage from '../hooks/useLocalStorage';
import { sessionsService } from '../services/supabaseService';
import { isSupabaseConfigured } from '../lib/supabase';
import { useAuth } from './AuthContext';

const SessionContext = createContext();

const sessionReducer = (state, action) => {
  switch (action.type) {
    case 'SET_SESSIONS':
      return {
        ...state,
        sessions: action.payload
      };
    case 'SET_CURRENT_SESSION':
      return {
        ...state,
        currentSessionId: action.payload
      };
    case 'CREATE_SESSION':
      const newSession = {
        id: action.payload.id,
        name: action.payload.name,
        description: action.payload.description || '',
        createdAt: action.payload.createdAt || new Date().toISOString(),
        lastUpdated: action.payload.lastUpdated || new Date().toISOString(),
        gameType: action.payload.gameType || 'Texas Hold\'em',
        stakes: action.payload.stakes || '',
        location: action.payload.location || '',
        isActive: action.payload.isActive !== undefined ? action.payload.isActive : true
      };
      return {
        ...state,
        sessions: [...state.sessions, newSession]
      };
    case 'UPDATE_SESSION':
      return {
        ...state,
        sessions: state.sessions.map(session =>
          session.id === action.payload.id
            ? { 
                ...session, 
                ...action.payload.updates, 
                lastUpdated: new Date().toISOString() 
              }
            : session
        )
      };
    case 'DELETE_SESSION':
      return {
        ...state,
        sessions: state.sessions.filter(session => session.id !== action.payload.sessionId),
        currentSessionId: state.currentSessionId === action.payload.sessionId 
          ? null 
          : state.currentSessionId
      };
    case 'ARCHIVE_SESSION':
      return {
        ...state,
        sessions: state.sessions.map(session =>
          session.id === action.payload.sessionId
            ? { ...session, isActive: false, lastUpdated: new Date().toISOString() }
            : session
        )
      };
    case 'RESTORE_SESSION':
      return {
        ...state,
        sessions: state.sessions.map(session =>
          session.id === action.payload.sessionId
            ? { ...session, isActive: true, lastUpdated: new Date().toISOString() }
            : session
        )
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload
      };
    default:
      return state;
  }
};

const initialState = {
  sessions: [],
  currentSessionId: null,
  loading: false
};

// Import the configuration check function

export const SessionProvider = ({ children }) => {
  const [state, dispatch] = useReducer(sessionReducer, initialState);
  const [storedSessions, setStoredSessions] = useLocalStorage('pokerSessions', []);
  const [storedCurrentSessionId, setStoredCurrentSessionId] = useLocalStorage('currentPokerSessionId', null);
  const [useSupabase, setUseSupabase] = useState(isSupabaseConfigured());
  const { user, isLoading: authLoading } = useAuth();

  // Load sessions on mount and when user/auth state changes
  useEffect(() => {
    // Don't load sessions if auth is still loading
    if (authLoading) {
      return;
    }

    const loadSessions = async () => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      // If user is logged out, clear sessions
      if (!user) {
        dispatch({ type: 'SET_SESSIONS', payload: [] });
        dispatch({ type: 'SET_CURRENT_SESSION', payload: null });
        dispatch({ type: 'SET_LOADING', payload: false });
        return;
      }
      
      if (useSupabase) {
        try {
          const sessions = await sessionsService.getAll();
          dispatch({ type: 'SET_SESSIONS', payload: sessions });
          // Load current session from localStorage (or you could store it in Supabase too)
          dispatch({ type: 'SET_CURRENT_SESSION', payload: storedCurrentSessionId });
        } catch (error) {
          console.error('Error loading sessions from Supabase:', error);
          // Fallback to localStorage if Supabase fails
          setUseSupabase(false);
          dispatch({ type: 'SET_SESSIONS', payload: storedSessions });
          dispatch({ type: 'SET_CURRENT_SESSION', payload: storedCurrentSessionId });
        }
      } else {
        dispatch({ type: 'SET_SESSIONS', payload: storedSessions });
        dispatch({ type: 'SET_CURRENT_SESSION', payload: storedCurrentSessionId });
      }
      
      dispatch({ type: 'SET_LOADING', payload: false });
    };

    loadSessions();
  }, [user, authLoading, useSupabase, storedSessions, storedCurrentSessionId]);

  // Sync to localStorage if not using Supabase, or as backup
  useEffect(() => {
    if (!useSupabase && state.sessions.length >= 0) {
      setStoredSessions(state.sessions);
    }
  }, [state.sessions, useSupabase, setStoredSessions]);

  useEffect(() => {
    if (state.currentSessionId !== storedCurrentSessionId) {
      setStoredCurrentSessionId(state.currentSessionId);
    }
  }, [state.currentSessionId, storedCurrentSessionId, setStoredCurrentSessionId]);

  const createSession = async (sessionData) => {
    if (useSupabase) {
      try {
        const newSession = await sessionsService.create(sessionData);
        dispatch({ type: 'CREATE_SESSION', payload: newSession });
        return newSession.id;
      } catch (error) {
        console.error('Error creating session:', error);
        throw error;
      }
    } else {
      // Fallback to localStorage
      const sessionId = Date.now().toString();
      const sessionWithId = { ...sessionData, id: sessionId };
      dispatch({ type: 'CREATE_SESSION', payload: sessionWithId });
      return sessionId;
    }
  };

  const updateSession = async (sessionId, updates) => {
    if (useSupabase) {
      try {
        const updatedSession = await sessionsService.update(sessionId, updates);
        dispatch({ type: 'UPDATE_SESSION', payload: { id: sessionId, updates: updatedSession } });
      } catch (error) {
        console.error('Error updating session:', error);
        throw error;
      }
    } else {
      dispatch({ type: 'UPDATE_SESSION', payload: { id: sessionId, updates } });
    }
  };

  const deleteSession = async (sessionId) => {
    if (useSupabase) {
      try {
        await sessionsService.delete(sessionId);
        dispatch({ type: 'DELETE_SESSION', payload: { sessionId } });
      } catch (error) {
        console.error('Error deleting session:', error);
        throw error;
      }
    } else {
      // Also clear the session's data from localStorage
      localStorage.removeItem(`pokerPlayers_${sessionId}`);
      localStorage.removeItem(`miscExpenses_${sessionId}`);
      dispatch({ type: 'DELETE_SESSION', payload: { sessionId } });
    }
  };

  const archiveSession = async (sessionId) => {
    if (useSupabase) {
      try {
        await sessionsService.archive(sessionId);
        dispatch({ type: 'ARCHIVE_SESSION', payload: { sessionId } });
      } catch (error) {
        console.error('Error archiving session:', error);
        throw error;
      }
    } else {
      dispatch({ type: 'ARCHIVE_SESSION', payload: { sessionId } });
    }
  };

  const restoreSession = async (sessionId) => {
    if (useSupabase) {
      try {
        await sessionsService.restore(sessionId);
        dispatch({ type: 'RESTORE_SESSION', payload: { sessionId } });
      } catch (error) {
        console.error('Error restoring session:', error);
        throw error;
      }
    } else {
      dispatch({ type: 'RESTORE_SESSION', payload: { sessionId } });
    }
  };

  const setCurrentSession = (sessionId) => {
    dispatch({ type: 'SET_CURRENT_SESSION', payload: sessionId });
  };

  const getCurrentSession = () => {
    return state.sessions.find(session => session.id === state.currentSessionId);
  };

  const getActiveSessions = () => {
    return state.sessions.filter(session => session.isActive);
  };

  const getArchivedSessions = () => {
    return state.sessions.filter(session => !session.isActive);
  };

  const getSessionById = (sessionId) => {
    return state.sessions.find(session => session.id === sessionId);
  };

  const generateSessionName = () => {
    const sessionNumber = state.sessions.length + 1;
    const currentDate = new Date().toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
    return `Poker Session ${sessionNumber} - ${currentDate}`;
  };

  const value = {
    ...state,
    createSession,
    updateSession,
    deleteSession,
    archiveSession,
    restoreSession,
    setCurrentSession,
    getCurrentSession,
    getActiveSessions,
    getArchivedSessions,
    getSessionById,
    generateSessionName
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

export default SessionContext;
